import { useState } from "react";
import { Button, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { RichTextField } from "@/components/RichTextField";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import {
  marketingUserGuide,
  USER_GUIDE_MAX_LENGTH,
  type MarketingUserGuide,
} from "@/api/marketingUserGuide.service";
import styles from "./UserGuide.module.css";

async function uploadImage(file: File): Promise<{ id: string; url: string }> {
  const { hash, url } = await marketingUserGuide.uploadImage(file);
  return { id: hash, url };
}

function GuideEditor({
  loaded,
  canWrite,
  onSaved,
  onError,
}: {
  loaded: MarketingUserGuide;
  canWrite: boolean;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [guide, setGuide] = useState(loaded);
  const [content, setContent] = useState(loaded.content);
  const [saving, setSaving] = useState(false);

  const tooLong = content.length > USER_GUIDE_MAX_LENGTH;
  const isDirty = content !== guide.content;

  async function save() {
    setSaving(true);
    try {
      const response = await marketingUserGuide.save(content);
      // Re-sync from the server response (it returns the sanitised HTML).
      setGuide(response.userGuide);
      setContent(response.userGuide.content);
      onSaved("User guide saved");
    } catch (error) {
      onError(error instanceof Error ? error.message : "The guide could not be saved");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        title="User guide"
        subtitle="Guidance shown read-only to firms in the marketing toolkit"
      >
        {canWrite && (
          <Button
            variant="contained"
            color="primary"
            disabled={saving || !isDirty || tooLong}
            onClick={save}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </PageHeader>

      <div className={styles.editor}>
        {tooLong && (
          <p className={styles.error}>
            The guide must be {USER_GUIDE_MAX_LENGTH.toLocaleString("en-US")} characters or fewer
          </p>
        )}

        {canWrite ? (
          <RichTextField
            value={content}
            onChange={setContent}
            preset="full"
            htmlTab
            mediaEnabled
            allowImageUrl
            uploadImage={uploadImage}
            label="User guide"
            minHeight={400}
          />
        ) : (
          <RichTextField
            value={content}
            onChange={() => {
              /* read-only: no permission to edit */
            }}
            preset="full"
            disabled
            label="User guide"
            minHeight={400}
          />
        )}

        {guide.updatedBy && (
          <p className={styles.savedAt}>
            Last changed by {guide.updatedBy} on {new Date(guide.updatedAt).toLocaleDateString()}
          </p>
        )}
      </div>
    </>
  );
}

export function MarketingToolkitUserGuidePage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("staff.marketing.read");
  const canWrite = hasPermission("staff.marketing.write");
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const { data, loading, error, refetch } = useQuery(() => marketingUserGuide.get(), []);

  if (!canRead) {
    return <UnauthorizedView />;
  }

  const guide = data?.userGuide ?? null;

  return (
    <div className={styles.wrapper}>
      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={loading} />}

      {loading && <LoadingState message="Loading the user guide…" />}

      {!loading && guide && (
        <GuideEditor
          loaded={guide}
          canWrite={canWrite}
          onSaved={message => setToast({ message, type: "success" })}
          onError={message => setToast({ message, type: "error" })}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default MarketingToolkitUserGuidePage;
